/**
 * `<ForgotPasswordRoute>` — `/forgot-password`
 *
 * Visual treatment follows the design-system "Dashboard/Auth/Forgot
 * Password" fixture: a request state (collect a work email to send a reset
 * link) and a confirmation state (success icon + resend / open-mail
 * actions), mirroring Sign In.
 *
 * NOTE: This is screens-only — there is no reset-link logic yet. Submitting
 * the form just flips to the confirmation state locally. The real
 * `POST /api/auth/...` request-reset + token-verify flow is a follow-up;
 * see the `TODO(reset-password)` markers below.
 */

import { Button, Field, FieldLabel, Input, Separator } from "@cometchat-ai/ui-dashboard";
import { ArrowLeft, MailCheck } from "../components/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { AuthLayout } from "../layouts/auth-layout";

function BackToSignIn({ label }: { label: string }) {
  return (
    <Link
      to="/login"
      className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      {label}
    </Link>
  );
}

export function ForgotPasswordRoute() {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // TODO(reset-password): call the request-reset endpoint here. For now
    // we only flip to the confirmation screen so the design is reviewable.
    setSent(true);
  }

  if (sent) {
    return (
      <AuthLayout
        icon={
          <span
            aria-hidden
            className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl border text-[color:var(--success-500)]"
            style={{
              background: "color-mix(in oklch, var(--success-500) 8%, transparent)",
              borderColor: "color-mix(in oklch, var(--success-500) 16%, transparent)",
            }}
          >
            <MailCheck className="h-6 w-6" />
          </span>
        }
        title={t("forgotPassword.sentTitle")}
        subtitle={
          <>
            {t("forgotPassword.sentTo")}{" "}
            <span className="font-medium text-foreground">
              {email || t("forgotPassword.emailPlaceholder")}
            </span>
            . {t("forgotPassword.sentExpiry")}
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              className="h-12 flex-1 rounded-xl hover:bg-secondary-hover"
              onClick={() => setSent(false)}
            >
              {t("forgotPassword.resend")}
            </Button>
            <Button asChild className="h-12 flex-1 rounded-xl">
              <a href="https://mail.google.com" target="_blank" rel="noreferrer">
                {t("forgotPassword.openMail")}
              </a>
            </Button>
          </div>
          <p className="text-center text-sm text-muted-foreground">
            {t("forgotPassword.spamHint")}
          </p>
          <Separator />
          <div className="flex justify-center">
            <BackToSignIn label={t("forgotPassword.back")} />
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("forgotPassword.title")} subtitle={t("forgotPassword.description")}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field className="gap-0">
          <FieldLabel htmlFor="forgot-email" className="mb-2">
            {t("forgotPassword.emailLabel")}
          </FieldLabel>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder={t("forgotPassword.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
          />
        </Field>

        <Button type="submit" className="h-10 w-full rounded-xl hover:bg-primary-hover">
          {t("forgotPassword.submit")}
        </Button>

        <div className="flex justify-center">
          <BackToSignIn label={t("forgotPassword.back")} />
        </div>
      </form>
    </AuthLayout>
  );
}
